type WebVitalMetric = {
  name: string
  value: number
  id: string
  delta?: number
}

/**
 * Lightweight Web Vitals reporting without the web-vitals package.
 * Uses native Performance APIs (LCP, FCP, TTFB) plus optional App Insights in production.
 */
export const reportWebVitals = (onPerfEntry?: (metric: WebVitalMetric) => void) => {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return
  }

  const report = onPerfEntry ?? defaultReporter

  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const last = entries[entries.length - 1] as PerformanceEntry & {
        renderTime?: number
        loadTime?: number
      }
      if (!last) return
      report({
        name: 'LCP',
        value: last.renderTime || last.loadTime || last.startTime,
        id: 'lcp',
      })
    }).observe({ type: 'largest-contentful-paint', buffered: true })
  } catch {
    // LCP not supported
  }

  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          report({ name: 'FCP', value: entry.startTime, id: 'fcp' })
        }
      }
    }).observe({ type: 'paint', buffered: true })
  } catch {
    // FCP not supported
  }

  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (nav) {
      report({
        name: 'TTFB',
        value: nav.responseStart,
        id: 'ttfb',
      })
    }
  } catch {
    // TTFB not supported
  }
}

const defaultReporter = (metric: WebVitalMetric) => {
  if (process.env.NODE_ENV !== 'production') {
    console.info(`[WebVitals] ${metric.name}:`, Math.round(metric.value))
    return
  }

  const appInsights = (window as unknown as { appInsights?: { trackMetric: (n: string, v: number, p: object) => void } })
    .appInsights
  if (appInsights?.trackMetric) {
    appInsights.trackMetric(metric.name, metric.value, {
      metricId: metric.id,
      delta: metric.delta,
    })
  }
}
